import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { 
    X, 
    Save, 
    Plus, 
    Trash2, 
    Image as ImageIcon, 
    Clock, 
    Calendar,
    Info,
    CheckCircle2,
    Loader2,
    AlertCircle,
    Layers,
    Star,
    Camera
} from 'lucide-react';

import { uploadImage, deleteImage } from '../../lib/storage';

interface ExpeditionEditorProps {
    onClose: () => void;
    onSave: (data: any) => Promise<void> | void;
    initialData?: any;
    theme: 'light' | 'dark';
    existingExpeditions?: any[];
}

function slugify(text: string): string {
    return text
        .toString()
        .toLowerCase()
        .normalize('NFD') // Decompose accents
        .replace(/[\u0300-\u036f]/g, '') // Remove accent marks
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/[^\w\-]+/g, '') // Remove non-word chars
        .replace(/\-\-+/g, '-') // Replace multiple hyphens
        .replace(/^-+/, '') // Trim leading hyphens
        .replace(/-+$/, ''); // Trim trailing hyphens
}

function getUniqueValue(baseValue: string, existingValues: string[]): { value: string; conflicted: boolean } {
    if (!baseValue) return { value: '', conflicted: false };
    let candidate = baseValue;
    let counter = 1;
    let conflicted = false;
    if (existingValues.includes(candidate)) {
        conflicted = true;
    }
    while (existingValues.includes(candidate)) {
        counter++;
        candidate = `${baseValue}-${counter}`;
    }
    return { value: candidate, conflicted };
}

function generateAutoIdAndSlug(
    name: string,
    category: string,
    existingIds: string[],
    existingSlugs: string[]
): { id: string; slug: string; idConflicted: boolean; slugConflicted: boolean } {
    const slugifiedName = slugify(name);
    if (!slugifiedName) return { id: '', slug: '', idConflicted: false, slugConflicted: false };

    // 1. Generate ID (strip category prefix if present)
    let baseId = slugifiedName;
    const categorySlug = slugify(category);
    if (categorySlug && baseId.startsWith(`${categorySlug}-`)) {
        baseId = baseId.slice(categorySlug.length + 1);
    } else if (categorySlug && baseId === categorySlug) {
        baseId = categorySlug;
    }

    // 2. Generate Slug (ensure category prefix is present)
    let baseSlug = slugifiedName;
    if (categorySlug && !baseSlug.startsWith(`${categorySlug}-`) && baseSlug !== categorySlug) {
        baseSlug = `${categorySlug}-${baseSlug}`;
    }

    // 3. Make them unique
    const idResult = getUniqueValue(baseId, existingIds);
    const slugResult = getUniqueValue(baseSlug, existingSlugs);

    return {
        id: idResult.value,
        slug: slugResult.value,
        idConflicted: idResult.conflicted,
        slugConflicted: slugResult.conflicted
    };
}

const ListEditor: React.FC<{
    title: string;
    list: string[];
    onChange: (newList: string[]) => void;
    placeholderSuggestions: string[];
    isDark: boolean;
}> = ({ title, list, onChange, placeholderSuggestions, isDark }) => {
    const inputClass = `w-full border p-3 text-sm outline-none transition-all rounded-[10px] ${isDark ? 'bg-black border-rock text-white focus:border-[#00d4ff]' : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-[#00d4ff]'}`;
    return (
        <div className="space-y-4">
            <h3 className="text-sm font-title uppercase tracking-widest text-[#00d4ff]">{title}</h3>
            {placeholderSuggestions.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {placeholderSuggestions.map(p => (
                        <button 
                            key={p} 
                            type="button"
                            onClick={() => !list.includes(p) && onChange([...list, p])} 
                            className={`px-3 py-1.5 border rounded-[10px] text-[8px] font-bold uppercase transition-all duration-200 ${
                                isDark 
                                ? 'border-rock text-gray-400 hover:bg-[#00d4ff]/10 hover:text-white' 
                                : 'border-gray-200 text-gray-500 hover:bg-[#00d4ff]/10 hover:text-gray-900 shadow-sm'
                            }`}
                        >
                            + {p}
                        </button>
                    ))}
                </div>
            )}
            <div className="space-y-3">
                {list.map((item, i) => (
                    <div key={i} className="flex gap-4 items-center group">
                        <input 
                            type="text" 
                            className={inputClass} 
                            value={item} 
                            onChange={e => {
                                const arr = [...list];
                                arr[i] = e.target.value;
                                onChange(arr);
                            }} 
                        />
                        <button 
                            type="button"
                            onClick={() => onChange(list.filter((_, idx) => idx !== i))} 
                            className="p-2 text-gray-500 hover:text-red-500 transition-colors"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                ))}
                <button 
                    type="button"
                    onClick={() => onChange([...list, ''])} 
                    className="text-[#00d4ff] text-[10px] font-black uppercase tracking-widest hover:underline"
                >
                    + Agregar ítem
                </button>
            </div>
        </div>
    );
};

const ExpeditionEditor: React.FC<ExpeditionEditorProps> = ({ onClose, onSave, initialData, theme, existingExpeditions = [] }) => {
    const isDark = theme === 'dark';
    const [activeSection, setActiveSection] = useState('basic');
    const [isUploading, setIsUploading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [notification, setNotification] = useState<{ message: string, type: 'error' | 'success' } | null>(null);
    const [newlyUploadedImages, setNewlyUploadedImages] = useState<string[]>([]);
    const [hasSaved, setHasSaved] = useState(false);
    const [customConfirm, setCustomConfirm] = useState<{ message: string; onConfirm: () => void; } | null>(null);

    const [isIdManuallyEdited, setIsIdManuallyEdited] = useState(!!initialData);
    const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(!!initialData);
    const [autoIdConflicted, setAutoIdConflicted] = useState(false);
    const [autoSlugConflicted, setAutoSlugConflicted] = useState(false);

    const existingIds = useMemo(() => {
        const list = existingExpeditions.map(e => e.id);
        if (initialData?.id) {
            return list.filter(id => id !== initialData.id);
        }
        return list;
    }, [existingExpeditions, initialData]);

    const existingSlugs = useMemo(() => {
        const list = existingExpeditions.map(e => e.slug);
        if (initialData?.slug) {
            return list.filter(s => s !== initialData.slug);
        }
        return list;
    }, [existingExpeditions, initialData]);

    const [formData, setFormData] = useState({
        id: initialData?.id || '',
        slug: initialData?.slug || '',
        name_es: initialData?.name_es || '',
        category: initialData?.category || 'trekking',
        duration: initialData?.duration || '',
        difficulty: initialData?.difficulty || 'mid',
        distance: initialData?.distance || '',
        price_from: initialData?.price_from || '',
        catchphrase_es: initialData?.catchphrase_es || '',
        desc_es: initialData?.desc_es || initialData?.description_es || '',
        itinerary_es: initialData?.itinerary_es || [] as any[],
        includes_es: initialData?.includes_es || [] as string[],
        excludes_es: initialData?.excludes_es || [] as string[],
        pricing_tiers: initialData?.pricing_tiers || [] as any[],
        gallery_layout: initialData?.gallery_layout || 'landscape',
        gallery: (() => {
            const list: { src: string; alt_es: string; alt_en: string; position?: string }[] = [];
            const coverSrc = initialData?.image || '';
            const existingGallery = initialData?.gallery || [];
            
            existingGallery.forEach((g: any) => {
                const src = typeof g === 'string' ? g : g?.src;
                if (!src) return;
                const alt_es = typeof g === 'string' ? '' : g?.alt_es || '';
                const alt_en = typeof g === 'string' ? '' : g?.alt_en || '';
                const position = typeof g === 'string' ? 'center' : g?.position || 'center';
                list.push({ src, alt_es, alt_en, position });
            });
            
            // Only add coverSrc if gallery is completely empty (prevents resurrecting deleted covers)
            if (list.length === 0 && coverSrc) {
                list.push({ src: coverSrc, alt_es: '', alt_en: '', position: 'center' });
            }
            
            return list;
        })(),
        variants: initialData?.variants || null,
        // Adaptable layout variables
        duration_type: initialData?.duration_type || 'full_day',
        itinerary_type: initialData?.itinerary_type || (initialData?.itinerary_es && initialData.itinerary_es.length > 0 ? (initialData.itinerary_es[0].time && initialData.itinerary_es[0].time.toLowerCase().includes('am') ? 'hourly' : 'daily') : 'none'),
        best_season_es: initialData?.best_season_es || '',
        price_flat: initialData?.price_flat || null,
        price_upgrades: initialData?.price_upgrades || [] as any[],
        recommendations_es: initialData?.recommendations_es || [] as string[],
        policies_es: initialData?.policies_es || [] as string[],
    });

    const [selectedPricingVariantId, setSelectedPricingVariantId] = useState<string>(() => {
        if (initialData?.variants && initialData.variants.length > 0) {
            // Default to first variant if general pricing is null or empty
            if (!initialData.pricing_tiers || initialData.pricing_tiers.length === 0) {
                return initialData.variants[0].id;
            }
        }
        return 'general';
    });

    const activePricingTiers = useMemo(() => {
        if (selectedPricingVariantId === 'general') {
            return formData.pricing_tiers || [];
        }
        const v = formData.variants?.find((varItem: any) => varItem.id === selectedPricingVariantId);
        return v?.pricing_tiers || [];
    }, [formData.pricing_tiers, formData.variants, selectedPricingVariantId]);

    const updateActivePricingTiers = (newTiers: any[]) => {
        if (selectedPricingVariantId === 'general') {
            setFormData(prev => ({ ...prev, pricing_tiers: newTiers }));
        } else {
            setFormData(prev => {
                if (!prev.variants) return prev;
                const updatedVariants = prev.variants.map((v: any) => {
                    if (v.id === selectedPricingVariantId) {
                        return { ...v, pricing_tiers: newTiers };
                    }
                    return v;
                });
                return { ...prev, variants: updatedVariants };
            });
        }
    };

    // Auto-hide notification
    useEffect(() => {
        if (notification) {
            const timer = setTimeout(() => setNotification(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [notification]);

    // Handle auto-generation of ID and Slug from name_es for new expeditions
    const handleNameEsChange = (val: string) => {
        setFormData(prev => {
            const updates: any = { name_es: val };
            if (!initialData) {
                const { id: newAutoId, slug: newAutoSlug, idConflicted, slugConflicted } = generateAutoIdAndSlug(
                    val,
                    prev.category,
                    existingIds,
                    existingSlugs
                );
                
                if (!isIdManuallyEdited) {
                    updates.id = newAutoId;
                    setAutoIdConflicted(idConflicted);
                }
                if (!isSlugManuallyEdited) {
                    updates.slug = newAutoSlug;
                    setAutoSlugConflicted(slugConflicted);
                }
            }
            return { ...prev, ...updates };
        });
    };

    const handleCategoryChange = (cat: string) => {
        setFormData(prev => {
            const updates: any = { category: cat };
            if (!initialData) {
                const { id: newAutoId, slug: newAutoSlug, idConflicted, slugConflicted } = generateAutoIdAndSlug(
                    prev.name_es,
                    cat,
                    existingIds,
                    existingSlugs
                );
                
                if (!isIdManuallyEdited) {
                    updates.id = newAutoId;
                    setAutoIdConflicted(idConflicted);
                }
                if (!isSlugManuallyEdited) {
                    updates.slug = newAutoSlug;
                    setAutoSlugConflicted(slugConflicted);
                }
            }
            return { ...prev, ...updates };
        });
    };

    const handleInternalSave = async () => {
        if (!formData.id.trim()) {
            setNotification({ message: 'El ID es obligatorio', type: 'error' });
            return;
        }
        if (!formData.name_es.trim()) {
            setNotification({ message: 'El nombre en español es obligatorio', type: 'error' });
            return;
        }
        if (!formData.slug.trim()) {
            setNotification({ message: 'El slug es obligatorio', type: 'error' });
            return;
        }

        // Check for duplicates
        if (!initialData && existingIds.includes(formData.id)) {
            setNotification({ message: 'El ID único ya está registrado por otra expedición', type: 'error' });
            return;
        }
        if (existingSlugs.includes(formData.slug)) {
            setNotification({ message: 'El slug ya está registrado por otra expedición', type: 'error' });
            return;
        }

        setIsSaving(true);
        try {
            // Ensure pricing integrity
            const preparedData = { ...formData };
            const numPriceFrom = (preparedData.price_from !== undefined && preparedData.price_from !== null && preparedData.price_from !== '') 
                ? Number(preparedData.price_from) 
                : null;
            const numPriceFlat = (preparedData.price_flat !== undefined && preparedData.price_flat !== null && preparedData.price_flat !== '') 
                ? Number(preparedData.price_flat) 
                : null;

            if (numPriceFlat && !numPriceFrom) {
                preparedData.price_from = numPriceFlat;
            }

            // Ensure pricing_tiers has at least 1 person tier if price_from or price_flat exists
            if (!numPriceFlat && (!preparedData.pricing_tiers || preparedData.pricing_tiers.length === 0)) {
                if (numPriceFrom) {
                    preparedData.pricing_tiers = [{ min_people: 1, price_per_person: numPriceFrom }];
                }
            }

            // If variants exist, ensure they don't have empty pricing_tiers
            if (preparedData.variants && Array.isArray(preparedData.variants)) {
                preparedData.variants = preparedData.variants.map((v: any) => {
                    if (!v.pricing_tiers || v.pricing_tiers.length === 0) {
                        return {
                            ...v,
                            pricing_tiers: (preparedData.pricing_tiers && preparedData.pricing_tiers.length > 0)
                                ? preparedData.pricing_tiers
                                : [{ min_people: 1, price_per_person: numPriceFrom || 100 }]
                        };
                    }
                    return v;
                });
            }

            await onSave(preparedData);
            setHasSaved(true);
        } catch (error: any) {
            console.error('Error saving expedition:', error);
            setNotification({ message: error?.message || 'Error al guardar expedición en la base de datos', type: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = async () => {
        const isDirty = newlyUploadedImages.length > 0 || 
                       formData.name_es !== (initialData?.name_es || '') ||
                       formData.desc_es !== (initialData?.desc_es || initialData?.description_es || '');

        if (isDirty && !hasSaved) {
            setCustomConfirm({
                message: '¿Seguro que quieres salir sin guardar? Se perderán los cambios.',
                onConfirm: async () => {
                    if (newlyUploadedImages.length > 0) {
                        setIsUploading(true);
                        try { await Promise.all(newlyUploadedImages.map(url => deleteImage(url))); }
                        catch (e) { console.error(e); }
                        finally { setIsUploading(false); }
                    }
                    onClose();
                }
            });
            return;
        }
        onClose();
    };

    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !customConfirm) handleClose(); };
        window.history.pushState({ editorOpen: true }, '');
        const handlePopState = () => handleClose();
        window.addEventListener('keydown', handleEsc);
        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('keydown', handleEsc);
            window.removeEventListener('popstate', handlePopState);
        };
    }, [newlyUploadedImages, hasSaved, formData, customConfirm]);

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        setIsUploading(true);
        try {
            const filesArray = Array.from(files);
            const successfulUrls: string[] = [];
            const errorMessages: string[] = [];

            for (const file of filesArray) {
                try {
                    const url = await uploadImage(file);
                    successfulUrls.push(url);
                } catch (err: any) {
                    errorMessages.push(err.message || `Error al procesar "${file.name}"`);
                }
            }

            if (successfulUrls.length > 0) {
                const newGalleryItems = successfulUrls.map(url => ({ src: url, alt_es: '', alt_en: '', position: 'center' }));
                setFormData(prev => ({ ...prev, gallery: [...prev.gallery, ...newGalleryItems] }));
                setNewlyUploadedImages(prev => [...prev, ...successfulUrls]);
            }

            if (errorMessages.length > 0) {
                setNotification({ 
                    message: errorMessages.slice(0, 2).join('. ') + (errorMessages.length > 2 ? ` (+${errorMessages.length - 2} errores)` : ''), 
                    type: 'error' 
                });
            } else if (successfulUrls.length > 0) {
                setNotification({ 
                    message: `${successfulUrls.length} foto${successfulUrls.length > 1 ? 's' : ''} subida${successfulUrls.length > 1 ? 's' : ''} con éxito`, 
                    type: 'success' 
                });
            }
        } catch (error: any) {
            console.error('Error uploading files to Supabase:', error);
            setNotification({ 
                message: `Error al subir imágenes: ${error.message || 'Verifica el archivo o tu conexión.'}`, 
                type: 'error' 
            });
        } finally { 
            setIsUploading(false); 
            if (e.target) e.target.value = '';
        }
    };

    const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        setIsUploading(true);
        try {
            const file = files[0];
            const url = await uploadImage(file);
            const newCoverItem = { src: url, alt_es: '', alt_en: '', position: 'center' };
            setFormData(prev => {
                const filtered = prev.gallery.filter((item: any) => item.src !== url);
                return {
                    ...prev,
                    gallery: [newCoverItem, ...filtered]
                };
            });
            setNewlyUploadedImages(prev => [...prev, url]);
            setNotification({ message: 'Foto de portada cambiada con éxito', type: 'success' });
        } catch (error: any) {
            console.error('Error uploading cover photo:', error);
            setNotification({ message: `Error al cambiar portada: ${error.message || 'Error desconocido'}`, type: 'error' });
        } finally {
            setIsUploading(false);
            if (e.target) e.target.value = '';
        }
    };

    const removeImage = async (url: string) => {
        setCustomConfirm({
            message: '¿Eliminar esta imagen?',
            onConfirm: async () => {
                try {
                    await deleteImage(url);
                    setFormData(prev => ({ ...prev, gallery: prev.gallery.filter((g: any) => g.src !== url) }));
                    setNewlyUploadedImages(prev => prev.filter(item => item !== url));
                } catch (e) { console.error(e); }
            }
        });
    };

    const sections = [
        { id: 'basic', label: 'Info Básica', icon: Info },
        { id: 'variants', label: 'Sub-paquetes / Variantes', icon: Layers },
        { id: 'itinerary', label: 'Itinerario', icon: Calendar },
        { id: 'pricing', label: 'Precios & Upgrades', icon: Clock },
        { id: 'logistics', label: 'Logística (Inc/Exc)', icon: CheckCircle2 },
        { id: 'policies', label: 'Políticas & Recs', icon: CheckCircle2 },
        { id: 'images', label: 'Fotos', icon: ImageIcon },
    ];

    const inputClass = `w-full border p-3 text-sm outline-none transition-all rounded-[10px] ${isDark ? 'bg-black border-rock text-white focus:border-[#00d4ff]' : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-[#00d4ff]'}`;
    const labelClass = "text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-2 block";

    const itineraryEs = formData.itinerary_es;
    const numDays = itineraryEs.length;

    return createPortal(
        <>
            <div 
                className="fixed inset-0 z-[9999] flex flex-col bg-black cursor-default animate-in fade-in duration-300"
            >
                <div 
                    className={`flex-1 flex flex-col h-full w-full overflow-hidden ${
                        isDark ? 'bg-[#1a1512] text-white' : 'bg-white text-gray-900'
                    }`}
                >
                    {/* Header */}
                    <div className={`p-6 lg:p-8 border-b flex items-center justify-between shrink-0 ${isDark ? 'border-rock' : 'border-gray-100'}`}>
                        <div className="flex items-center gap-6">
                            <button onClick={handleClose} className="p-2 hover:bg-red-500/10 text-gray-400 hover:text-red-500 transition-all rounded-[10px]"><X /></button>
                            <h2 className="text-xl font-title uppercase tracking-tight">{initialData ? 'Editar' : 'Nueva'} Expedición</h2>
                        </div>
                        <button 
                            onClick={handleInternalSave} 
                            disabled={isUploading || isSaving} 
                            className={`bg-[#00d4ff] text-black px-10 py-4 font-title text-sm uppercase tracking-widest hover:bg-white transition-all rounded-[10px] shadow-xl flex items-center gap-2 ${(isUploading || isSaving) ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                            {(isUploading || isSaving) ? <Loader2 className="animate-spin w-4 h-4" /> : <Save className="w-4 h-4" />}
                            {isSaving ? 'Guardando...' : 'Guardar'}
                        </button>
                    </div>

                    <div className="flex-1 flex overflow-hidden">
                        {/* Sidebar */}
                        <aside className={`w-20 lg:w-64 border-r p-6 space-y-2 shrink-0 ${isDark ? 'border-rock bg-black/20' : 'border-gray-100 bg-gray-50/50'}`}>
                            {sections.map((s) => (
                                <button 
                                    key={s.id} 
                                    onClick={() => setActiveSection(s.id)} 
                                    className={`w-full flex items-center gap-3 px-4 py-4 text-[10px] font-bold uppercase tracking-widest rounded-[10px] transition-all ${
                                        activeSection === s.id 
                                        ? 'bg-[#00d4ff] text-black shadow-lg shadow-[#00d4ff]/20' 
                                        : isDark 
                                            ? 'text-gray-500 hover:text-white hover:bg-white/5' 
                                            : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                                    }`}
                                >
                                    <s.icon className="w-4 h-4" />
                                    <span className="hidden lg:inline">{s.label}</span>
                                </button>
                            ))}
                        </aside>

                        {/* Content Area */}
                        <div className="flex-1 p-6 lg:p-12 overflow-y-auto custom-scrollbar">
                            {activeSection === 'variants' && (
                                <div className="space-y-6 animate-in fade-in duration-500">
                                    <div>
                                        <h3 className="text-lg font-title uppercase tracking-tight font-bold">Sub-paquetes o Variantes</h3>
                                        <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mt-1">
                                            Permite vender diferentes opciones o sectores bajo una misma expedición (ej. diferentes cerros, duraciones o estilos de escalada).
                                        </p>
                                    </div>

                                    <div className={`p-6 border rounded-[10px] ${isDark ? 'bg-black/20 border-rock' : 'bg-gray-50 border-gray-100 shadow-sm'}`}>
                                        <label className="flex items-center gap-3 cursor-pointer">
                                            <input 
                                                type="checkbox"
                                                checked={!!formData.variants}
                                                onChange={e => {
                                                    if (e.target.checked) {
                                                        setFormData({
                                                            ...formData,
                                                            variants: [{
                                                                id: 'nueva-variante',
                                                                name_es: 'Nueva Variante',
                                                                name_en: 'New Variant',
                                                                duration: 'Full Day',
                                                                info: 'Descripción corta de la variante',
                                                                pricing_tiers: []
                                                            }]
                                                        });
                                                    } else {
                                                        setFormData({ ...formData, variants: null });
                                                    }
                                                }}
                                                className="w-4 h-4 rounded text-[#00d4ff] focus:ring-[#00d4ff] bg-transparent border-rock"
                                            />
                                            <span className="text-xs uppercase font-bold tracking-wider">Habilitar sub-paquetes o variantes para esta expedición</span>
                                        </label>
                                    </div>

                                    {formData.variants && (
                                        <div className="space-y-6">
                                            <div className="flex items-center justify-between border-b border-rock/20 pb-2">
                                                <h4 className="font-title text-sm uppercase text-gray-400">Variantes Configuradas</h4>
                                                <span className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">{(formData.variants || []).length} Opciones</span>
                                            </div>

                                            <div className="space-y-6">
                                                {(formData.variants || []).map((v: any, idx: number) => (
                                                    <div key={idx} className={`border rounded-[10px] p-6 space-y-4 transition-colors ${
                                                        isDark ? 'bg-black/20 border-rock' : 'bg-gray-50 border-gray-100 shadow-inner'
                                                    }`}>
                                                        <div className="flex justify-between items-center pb-2 border-b border-rock/30">
                                                            <h4 className="font-title text-sm uppercase text-[#00d4ff]">
                                                                Variante #{idx + 1}
                                                            </h4>
                                                            <button 
                                                                type="button"
                                                                onClick={() => {
                                                                    const updated = formData.variants.filter((_: any, i: number) => i !== idx);
                                                                    setFormData({ ...formData, variants: updated.length > 0 ? updated : null });
                                                                }}
                                                                className="text-red-500 hover:text-red-400 text-[10px] font-bold uppercase tracking-widest flex items-center gap-1 transition-colors"
                                                            >
                                                                <Trash2 className="w-3.5 h-3.5" /> Eliminar
                                                            </button>
                                                        </div>

                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                            <div>
                                                                <label className={labelClass}>ID Único de Variante <span className="text-red-500">*</span> (Ej: mackay, chabela, el-aguila)</label>
                                                                <input 
                                                                    type="text" 
                                                                    className={inputClass}
                                                                    value={v.id} 
                                                                    onChange={e => {
                                                                        const updated = [...formData.variants];
                                                                        updated[idx] = { ...updated[idx], id: slugify(e.target.value) };
                                                                        setFormData({ ...formData, variants: updated });
                                                                    }} 
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className={labelClass}>Duración (Ej: Full Day, Half Day, 2 Horas)</label>
                                                                <input 
                                                                    type="text" 
                                                                    className={inputClass}
                                                                    value={v.duration || ''} 
                                                                    onChange={e => {
                                                                        const updated = [...formData.variants];
                                                                        updated[idx] = { ...updated[idx], duration: e.target.value };
                                                                        setFormData({ ...formData, variants: updated });
                                                                    }} 
                                                                />
                                                            </div>
                                                        </div>

                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                            <div>
                                                                <label className={labelClass}>Nombre (Español) <span className="text-red-500">*</span></label>
                                                                <input 
                                                                    type="text" 
                                                                    className={inputClass}
                                                                    value={v.name_es} 
                                                                    onChange={e => {
                                                                        const updated = [...formData.variants];
                                                                        updated[idx] = { ...updated[idx], name_es: e.target.value };
                                                                        setFormData({ ...formData, variants: updated });
                                                                    }} 
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className={labelClass}>Nombre (Inglés) <span className="text-red-500">*</span></label>
                                                                <input 
                                                                    type="text" 
                                                                    className={inputClass}
                                                                    value={v.name_en} 
                                                                    onChange={e => {
                                                                        const updated = [...formData.variants];
                                                                        updated[idx] = { ...updated[idx], name_en: e.target.value };
                                                                        setFormData({ ...formData, variants: updated });
                                                                    }} 
                                                                />
                                                            </div>
                                                        </div>

                                                        <div>
                                                            <label className={labelClass}>Información Corta / Descripción (Ej: Ruta de 300MTS. Dificultad principiante/medio.)</label>
                                                            <input 
                                                                type="text" 
                                                                className={inputClass}
                                                                value={v.info || ''} 
                                                                onChange={e => {
                                                                    const updated = [...formData.variants];
                                                                    updated[idx] = { ...updated[idx], info: e.target.value };
                                                                    setFormData({ ...formData, variants: updated });
                                                                }} 
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            <button 
                                                type="button"
                                                onClick={() => {
                                                    const nextNum = (formData.variants || []).length + 1;
                                                    setFormData({
                                                        ...formData,
                                                        variants: [...(formData.variants || []), {
                                                            id: `variante-${nextNum}`,
                                                            name_es: `Nueva Variante ${nextNum}`,
                                                            name_en: `New Variant ${nextNum}`,
                                                            duration: 'Full Day',
                                                            info: '',
                                                            pricing_tiers: []
                                                        }],
                                                    });
                                                }} 
                                                className={`w-full py-10 border-2 border-dashed rounded-[10px] text-[10px] font-bold uppercase tracking-widest text-center transition-all bg-black/5 ${
                                                    isDark ? 'border-rock text-gray-500 hover:border-[#00d4ff] hover:text-[#00d4ff]' : 'border-gray-300 text-gray-500 hover:border-[#00d4ff] hover:text-[#00d4ff]'
                                                }`}
                                            >
                                                + Agregar Sub-paquete / Variante
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeSection === 'basic' && (
                                <div className="space-y-6 animate-in fade-in duration-500">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        <div className="md:col-span-2">
                                            <label className={labelClass}>Nombre de la Expedición <span className="text-red-500">*</span></label>
                                            <input 
                                                type="text" 
                                                className={inputClass} 
                                                value={formData.name_es} 
                                                onChange={e => handleNameEsChange(e.target.value)} 
                                                placeholder="Ej: Laguna Cerro Castillo"
                                            />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Categoría</label>
                                            <select 
                                                className={inputClass} 
                                                value={formData.category} 
                                                onChange={e => handleCategoryChange(e.target.value)}
                                            >
                                                <option value="trekking">Trekking</option>
                                                <option value="climbing">Escalada</option>
                                                <option value="water">Agua</option>
                                                <option value="wellness">Wellness</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className={labelClass}>ID único <span className="text-red-500">*</span> (Ej: laguna)</label>
                                            <div className="relative">
                                                <input 
                                                    type="text" 
                                                    className={`${inputClass} pr-16 ${!initialData && !isIdManuallyEdited ? 'border-dashed border-[#00d4ff]/50 bg-black/10' : ''}`} 
                                                    value={formData.id} 
                                                    disabled={!!initialData}
                                                    onChange={e => {
                                                        const val = e.target.value;
                                                        if (!val) {
                                                            setIsIdManuallyEdited(false);
                                                            const { id: newAutoId, idConflicted } = generateAutoIdAndSlug(
                                                                formData.name_es,
                                                                formData.category,
                                                                existingIds,
                                                                existingSlugs
                                                            );
                                                            setFormData(prev => ({ ...prev, id: newAutoId }));
                                                            setAutoIdConflicted(idConflicted);
                                                        } else {
                                                            setIsIdManuallyEdited(true);
                                                            setFormData(prev => ({ ...prev, id: slugify(val) }));
                                                        }
                                                    }} 
                                                />
                                                {!initialData && !isIdManuallyEdited && formData.id && (
                                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[9px] font-black uppercase text-[#00d4ff] bg-[#00d4ff]/10 px-2 py-1 rounded-[5px] pointer-events-none select-none">
                                                        Auto
                                                    </span>
                                                )}
                                                {!initialData && isIdManuallyEdited && (
                                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[9px] font-black uppercase text-amber-500 bg-amber-500/10 px-2 py-1 rounded-[5px] pointer-events-none select-none">
                                                        Manual
                                                    </span>
                                                )}
                                            </div>
                                            {!initialData && !isIdManuallyEdited && autoIdConflicted && (
                                                <p className="text-amber-500 text-[10px] font-bold uppercase mt-1.5 flex items-center gap-1.5 animate-in fade-in duration-300">
                                                    <span>⚠️</span> El ID ya existe. Se añadió un sufijo numérico automáticamente.
                                                </p>
                                            )}
                                            {!initialData && isIdManuallyEdited && existingIds.includes(formData.id) && (
                                                <p className="text-red-500 text-[10px] font-bold uppercase mt-1.5 flex items-center gap-1.5 animate-in fade-in duration-300">
                                                    <span>⚠️</span> Este ID ya existe. Por favor usa otro para evitar conflictos.
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <label className={labelClass}>Slug <span className="text-red-500">*</span> (Ej: trekking-laguna-cerro-castillo)</label>
                                            <div className="relative">
                                                <input 
                                                    type="text" 
                                                    className={`${inputClass} pr-16 ${!initialData && !isSlugManuallyEdited ? 'border-dashed border-[#00d4ff]/50 bg-black/10' : ''}`} 
                                                    value={formData.slug} 
                                                    onChange={e => {
                                                        const val = e.target.value;
                                                        if (!val) {
                                                            setIsSlugManuallyEdited(false);
                                                            const { slug: newAutoSlug, slugConflicted } = generateAutoIdAndSlug(
                                                                formData.name_es,
                                                                formData.category,
                                                                existingIds,
                                                                existingSlugs
                                                            );
                                                            setFormData(prev => ({ ...prev, slug: newAutoSlug }));
                                                            setAutoSlugConflicted(slugConflicted);
                                                        } else {
                                                            setIsSlugManuallyEdited(true);
                                                            setFormData(prev => ({ ...prev, slug: slugify(val) }));
                                                        }
                                                    }} 
                                                />
                                                {!initialData && !isSlugManuallyEdited && formData.slug && (
                                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[9px] font-black uppercase text-[#00d4ff] bg-[#00d4ff]/10 px-2 py-1 rounded-[5px] pointer-events-none select-none">
                                                        Auto
                                                    </span>
                                                )}
                                                {!initialData && isSlugManuallyEdited && (
                                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[9px] font-black uppercase text-amber-500 bg-amber-500/10 px-2 py-1 rounded-[5px] pointer-events-none select-none">
                                                        Manual
                                                    </span>
                                                )}
                                            </div>
                                            {!initialData && !isSlugManuallyEdited && autoSlugConflicted && (
                                                <p className="text-amber-500 text-[10px] font-bold uppercase mt-1.5 flex items-center gap-1.5 animate-in fade-in duration-300">
                                                    <span>⚠️</span> El slug ya existe. Se añadió un sufijo numérico automáticamente.
                                                </p>
                                            )}
                                            {isSlugManuallyEdited && existingSlugs.includes(formData.slug) && (
                                                <p className="text-red-500 text-[10px] font-bold uppercase mt-1.5 flex items-center gap-1.5 animate-in fade-in duration-300">
                                                    <span>⚠️</span> Este Slug ya existe. Por favor usa otro para evitar conflictos.
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                                        <div>
                                            <label className={labelClass}>Dificultad</label>
                                            <select 
                                                className={inputClass} 
                                                value={formData.difficulty} 
                                                onChange={e => setFormData({...formData, difficulty: e.target.value as any})}
                                            >
                                                <option value="low">Baja (Low)</option>
                                                <option value="mid">Media (Mid)</option>
                                                <option value="high">Alta (High)</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className={labelClass}>Duración (Ej: 4 Días / 3 Noches)</label>
                                            <input 
                                                type="text" 
                                                className={inputClass} 
                                                value={formData.duration} 
                                                onChange={e => setFormData({...formData, duration: e.target.value})} 
                                            />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Tipo de Duración</label>
                                            <select
                                                className={inputClass}
                                                value={formData.duration_type}
                                                onChange={e => setFormData({...formData, duration_type: e.target.value as any})}
                                            >
                                                <option value="half_day">Media Jornada (Half Day)</option>
                                                <option value="full_day">Jornada Completa (Full Day)</option>
                                                <option value="multi_day">Varios Días (Multi Day)</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className={labelClass}>Distancia total (Ej: ~53 km)</label>
                                            <input 
                                                type="text" 
                                                className={inputClass} 
                                                value={formData.distance} 
                                                onChange={e => setFormData({...formData, distance: e.target.value})} 
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className={labelClass}>Precio desde USD (Ej: 500)</label>
                                            <input 
                                                type="number" 
                                                className={inputClass} 
                                                value={formData.price_from} 
                                                onChange={e => setFormData({...formData, price_from: e.target.value ? parseInt(e.target.value) : ''})} 
                                            />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Mejor Época (Ej: Octubre a Abril)</label>
                                            <input 
                                                type="text" 
                                                className={inputClass} 
                                                value={formData.best_season_es} 
                                                onChange={e => setFormData({...formData, best_season_es: e.target.value})} 
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className={labelClass}>Frase Destacada / Catchphrase</label>
                                        <input 
                                            type="text" 
                                            className={inputClass} 
                                            value={formData.catchphrase_es} 
                                            onChange={e => setFormData({...formData, catchphrase_es: e.target.value})} 
                                        />
                                    </div>

                                    <div>
                                        <label className={labelClass}>Descripción</label>
                                        <textarea 
                                            className={`${inputClass} min-h-[160px]`} 
                                            value={formData.desc_es} 
                                            onChange={e => setFormData({...formData, desc_es: e.target.value})} 
                                        />
                                    </div>

                                    <div className={`flex items-start gap-3 p-4 rounded-[10px] border ${
                                        isDark ? 'bg-[#00d4ff]/5 border-[#00d4ff]/20 text-[#00d4ff]' : 'bg-cyan-50 border-cyan-200 text-cyan-700'
                                    }`}>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mt-0.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
                                        <p className="text-[9px] font-bold uppercase tracking-widest leading-relaxed">
                                            Al guardar, el sistema traduce automáticamente al inglés, portugués, francés, italiano y alemán.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {activeSection === 'itinerary' && (
                                <div className="space-y-6 animate-in fade-in duration-500">
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                        <div>
                                            <h3 className="text-lg font-title uppercase tracking-tight font-bold">Itinerario del Viaje</h3>
                                            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mt-1">Configura si la expedición tiene un itinerario diario, por horas o ninguno.</p>
                                        </div>
                                        <div className="w-full md:w-64">
                                            <label className={labelClass}>Tipo de Itinerario</label>
                                            <select
                                                className={inputClass}
                                                value={formData.itinerary_type}
                                                onChange={e => setFormData({...formData, itinerary_type: e.target.value as any})}
                                            >
                                                <option value="none">Sin Itinerario (None)</option>
                                                <option value="daily">Por Días (Daily - Ej: Travesía 4 días)</option>
                                                <option value="hourly">Por Horas (Hourly - Ej: Full day/Half day)</option>
                                            </select>
                                        </div>
                                    </div>

                                    {formData.itinerary_type !== 'none' && (
                                        <div className="space-y-6">
                                            <div className="flex items-center justify-between">
                                                <h4 className="font-title text-sm uppercase text-gray-400">Puntos del Itinerario</h4>
                                                <span className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">{numDays} Puntos Configurados</span>
                                            </div>

                                            <div className="space-y-6">
                                                {Array.from({ length: numDays }).map((_, idx) => {
                                                    const dayNum = idx + 1;
                                                    const itemEs = itineraryEs.find((item: any) => item.day === dayNum) || { day: dayNum, title: '', route: '', distance: '', time: '', desc: '', hito: '' };

                                                    return (
                                                        <div key={dayNum} className={`border rounded-[10px] p-6 space-y-4 transition-colors ${
                                                            isDark ? 'bg-black/20 border-rock' : 'bg-gray-50 border-gray-100 shadow-inner'
                                                        }`}>
                                                            <div className="flex justify-between items-center pb-2 border-b border-rock/30">
                                                                <h4 className="font-title text-sm uppercase text-[#00d4ff]">
                                                                    {formData.itinerary_type === 'hourly' ? `Punto / Hora ${dayNum}` : `Día ${dayNum}`}
                                                                </h4>
                                                                <button 
                                                                    type="button"
                                                                    onClick={() => {
                                                                        const newEs = itineraryEs.filter((item: any) => item.day !== dayNum).map((item: any) => item.day > dayNum ? { ...item, day: item.day - 1 } : item);
                                                                        setFormData({ ...formData, itinerary_es: newEs });
                                                                    }}
                                                                    className="text-red-500 hover:text-red-400 text-[10px] font-bold uppercase tracking-widest flex items-center gap-1 transition-colors"
                                                                >
                                                                    <Trash2 className="w-3.5 h-3.5" /> Eliminar
                                                                </button>
                                                            </div>
                                                            
                                                            <div>
                                                                <label className={labelClass}>Título (Ej: Acceso a Las Horquetas)</label>
                                                                <input 
                                                                    type="text" 
                                                                    className={inputClass} 
                                                                    value={itemEs.title || ''} 
                                                                    onChange={(e) => {
                                                                        const newEs = [...itineraryEs];
                                                                        const existIdx = newEs.findIndex((item: any) => item.day === dayNum);
                                                                        if (existIdx >= 0) { newEs[existIdx] = { ...newEs[existIdx], title: e.target.value }; }
                                                                        else { newEs.push({ day: dayNum, title: e.target.value, desc: '' }); }
                                                                        setFormData({ ...formData, itinerary_es: newEs });
                                                                    }} 
                                                                />
                                                            </div>

                                                            <div>
                                                                <label className={labelClass}>Trayecto / Ruta (Ej: Puesto Control – Campamento Turbio)</label>
                                                                <input 
                                                                    type="text" 
                                                                    className={inputClass} 
                                                                    value={itemEs.route || ''} 
                                                                    onChange={(e) => {
                                                                        const newEs = [...itineraryEs];
                                                                        const existIdx = newEs.findIndex((item: any) => item.day === dayNum);
                                                                        if (existIdx >= 0) { newEs[existIdx] = { ...newEs[existIdx], route: e.target.value }; }
                                                                        else { newEs.push({ day: dayNum, route: e.target.value, desc: '' }); }
                                                                        setFormData({ ...formData, itinerary_es: newEs });
                                                                    }} 
                                                                />
                                                            </div>

                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                <div>
                                                                    <label className={labelClass}>Distancia (Ej: 15 km)</label>
                                                                    <input 
                                                                        type="text" 
                                                                        className={inputClass} 
                                                                        value={itemEs.distance || ''} 
                                                                        onChange={(e) => {
                                                                            const newEs = [...itineraryEs];
                                                                            const existIdx = newEs.findIndex((item: any) => item.day === dayNum);
                                                                            if (existIdx >= 0) { newEs[existIdx] = { ...newEs[existIdx], distance: e.target.value }; }
                                                                            else { newEs.push({ day: dayNum, distance: e.target.value, desc: '' }); }
                                                                            setFormData({ ...formData, itinerary_es: newEs });
                                                                        }} 
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <label className={labelClass}>{formData.itinerary_type === 'hourly' ? 'Hora (Ej: 08:30 AM)' : 'Tiempo de Caminata (Ej: 6-7 horas)'}</label>
                                                                    <input 
                                                                        type="text" 
                                                                        className={inputClass} 
                                                                        value={itemEs.time || ''} 
                                                                        onChange={(e) => {
                                                                            const newEs = [...itineraryEs];
                                                                            const existIdx = newEs.findIndex((item: any) => item.day === dayNum);
                                                                            if (existIdx >= 0) { newEs[existIdx] = { ...newEs[existIdx], time: e.target.value }; }
                                                                            else { newEs.push({ day: dayNum, time: e.target.value, desc: '' }); }
                                                                            setFormData({ ...formData, itinerary_es: newEs });
                                                                        }} 
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div>
                                                                <label className={labelClass}>Descripción del día</label>
                                                                <textarea 
                                                                    className={`${inputClass} min-h-[90px]`} 
                                                                    value={itemEs.desc || ''} 
                                                                    onChange={(e) => {
                                                                        const newEs = [...itineraryEs];
                                                                        const existIdx = newEs.findIndex((item: any) => item.day === dayNum);
                                                                        if (existIdx >= 0) { newEs[existIdx] = { ...newEs[existIdx], desc: e.target.value }; }
                                                                        else { newEs.push({ day: dayNum, desc: e.target.value }); }
                                                                        setFormData({ ...formData, itinerary_es: newEs });
                                                                    }} 
                                                                />
                                                            </div>

                                                            <div>
                                                                <label className={labelClass}>Hito destacado (Ej: Cumbre del Cerro El Palo)</label>
                                                                <input 
                                                                    type="text" 
                                                                    className={inputClass} 
                                                                    value={itemEs.hito || ''} 
                                                                    onChange={(e) => {
                                                                        const newEs = [...itineraryEs];
                                                                        const existIdx = newEs.findIndex((item: any) => item.day === dayNum);
                                                                        if (existIdx >= 0) { newEs[existIdx] = { ...newEs[existIdx], hito: e.target.value }; }
                                                                        else { newEs.push({ day: dayNum, hito: e.target.value, desc: '' }); }
                                                                        setFormData({ ...formData, itinerary_es: newEs });
                                                                    }} 
                                                                />
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            <button 
                                                type="button"
                                                onClick={() => {
                                                    const nextDay = numDays + 1;
                                                    setFormData({
                                                        ...formData,
                                                        itinerary_es: [...itineraryEs, { day: nextDay, title: '', route: '', distance: '', time: '', desc: '', hito: '' }],
                                                    });
                                                }} 
                                                className={`w-full py-10 border-2 border-dashed rounded-[10px] text-[10px] font-bold uppercase tracking-widest text-center transition-all bg-black/5 ${
                                                    isDark ? 'border-rock text-gray-500 hover:border-[#00d4ff] hover:text-[#00d4ff]' : 'border-gray-300 text-gray-500 hover:border-[#00d4ff] hover:text-[#00d4ff]'
                                                }`}
                                            >
                                                {formData.itinerary_type === 'hourly' ? `+ Agregar Punto / Hora (${numDays + 1})` : `+ Agregar Día al Itinerario (Día ${numDays + 1})`}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeSection === 'logistics' && (
                                <div className="space-y-12 animate-in fade-in duration-500">
                                    <ListEditor 
                                        title="Incluye" 
                                        list={formData.includes_es} 
                                        onChange={list => setFormData({...formData, includes_es: list})} 
                                        placeholderSuggestions={['Guía certificado ANGM y WFR, Bilingüe.', 'Box Lunch', 'Transporte', 'Entrada al Parque Nacional', 'Kit de primeros auxilios']}
                                        isDark={isDark}
                                    />
                                    <div className="border-t border-rock/30 my-8"></div>
                                    <ListEditor 
                                        title="No Incluye" 
                                        list={formData.excludes_es} 
                                        onChange={list => setFormData({...formData, excludes_es: list})} 
                                        placeholderSuggestions={['Zapatos de trekking', 'Ropa técnica por capas', 'Mochila de expedición', 'Seguro contra accidentes']}
                                        isDark={isDark}
                                    />
                                </div>
                            )}

                            {activeSection === 'images' && (
                                <div className="space-y-8 animate-in fade-in duration-500">
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-6 border-b border-rock/20">
                                        <div>
                                            <h3 className="text-lg font-title uppercase tracking-tight">Fotos de la Expedición</h3>
                                            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mt-1">
                                                Control total de imágenes. La primera foto es siempre la Portada Principal en las tarjetas y cabeceras.
                                            </p>
                                        </div>
                                        <div className="w-full md:w-80">
                                            <label className={labelClass}>Disposición / Diseño de Galería</label>
                                            <select
                                                className={inputClass}
                                                value={formData.gallery_layout}
                                                onChange={e => setFormData({ ...formData, gallery_layout: e.target.value as any })}
                                            >
                                                <option value="landscape">Horizontal Recortada (Cover con Parallax)</option>
                                                <option value="contain">Horizontal Completa (Fit con Fondo Borroso)</option>
                                                <option value="portrait">Vertical de Retrato (Fit con Fondo Borroso)</option>
                                            </select>
                                            <p className="text-[8px] text-gray-500 mt-1 uppercase tracking-wider">
                                                * El modo <strong>Horizontal Completa (Fit)</strong> evita recortar las montañas.
                                            </p>
                                        </div>
                                    </div>

                                    {/* SECCIÓN DESTACADA: PORTADA PRINCIPAL */}
                                    <div className={`p-6 border rounded-[12px] ${isDark ? 'bg-gradient-to-r from-black/40 to-[#00d4ff]/5 border-rock' : 'bg-gradient-to-r from-gray-50 to-cyan-50/30 border-gray-200 shadow-sm'}`}>
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-2">
                                                <Star className="w-5 h-5 text-[#00d4ff] fill-[#00d4ff]" />
                                                <h4 className="text-sm font-title uppercase tracking-wider font-bold">Foto de Portada Principal</h4>
                                            </div>
                                            <label className="flex items-center gap-2 px-4 py-2 bg-[#00d4ff] text-black rounded-[8px] text-[10px] font-black uppercase tracking-wider cursor-pointer hover:bg-white transition-all shadow-md">
                                                <Camera className="w-3.5 h-3.5" />
                                                <span>{isUploading ? 'Subiendo...' : 'Cambiar Portada'}</span>
                                                <input type="file" accept="image/jpeg,image/png,image/webp,image/jpg" className="hidden" onChange={handleCoverUpload} disabled={isUploading} />
                                            </label>
                                        </div>

                                        {formData.gallery.length > 0 ? (
                                            <div className="flex flex-col sm:flex-row items-center gap-6">
                                                <div className="w-full sm:w-64 h-40 rounded-[10px] overflow-hidden relative border border-[#00d4ff]/40 shadow-xl shrink-0 bg-black/50">
                                                    <img 
                                                        src={formData.gallery[0].src} 
                                                        alt="Portada" 
                                                        className={`w-full h-full object-cover ${formData.gallery[0].position === 'top' ? 'object-top' : formData.gallery[0].position === 'bottom' ? 'object-bottom' : 'object-center'}`} 
                                                    />
                                                    <span className="bg-[#00d4ff] text-black px-2.5 py-0.5 text-[8px] font-black uppercase rounded tracking-wider absolute top-2 left-2 shadow">
                                                        Portada Activa
                                                    </span>
                                                </div>
                                                <div className="flex-1 space-y-3 w-full">
                                                    <div>
                                                        <label className="text-[9px] font-bold uppercase tracking-widest text-gray-400 mb-1 block">Alineación / Enfoque de Portada</label>
                                                        <select
                                                            className={inputClass}
                                                            value={formData.gallery[0].position || 'center'}
                                                            onChange={e => {
                                                                const updated = [...formData.gallery];
                                                                updated[0] = { ...updated[0], position: e.target.value };
                                                                setFormData({ ...formData, gallery: updated });
                                                            }}
                                                        >
                                                            <option value="center">Centro (Default)</option>
                                                            <option value="top">Alinear Arriba (Cumbres / Montañas)</option>
                                                            <option value="bottom">Alinear Abajo (Valles / Suelos)</option>
                                                        </select>
                                                    </div>
                                                    <p className="text-[9px] text-gray-500">
                                                        Esta imagen aparecerá en las tarjetas de expediciones de la portada, listados principales y cabeceras de redes sociales.
                                                    </p>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="py-8 text-center text-gray-500 text-xs font-bold uppercase tracking-wider">
                                                No hay fotos en esta expedición. Haz clic en "Cambiar Portada" para subir la primera foto.
                                            </div>
                                        )}
                                    </div>

                                    {/* GALERÍA COMPLETA */}
                                    <div className="pt-4 border-t border-rock/20">
                                        <div className="flex items-center justify-between mb-4">
                                            <h4 className="text-xs font-title uppercase tracking-widest text-gray-400">Todas las Fotos ({formData.gallery.length})</h4>
                                            <span className="text-[9px] text-gray-500 font-bold uppercase">Usa los botones para cambiar la portada o eliminar</span>
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                                            <label className="aspect-square border-2 border-dashed border-rock flex flex-col items-center justify-center cursor-pointer rounded-[10px] hover:bg-[#00d4ff]/5 transition-all text-gray-400 hover:text-[#00d4ff] bg-black/5 p-4 text-center">
                                                {isUploading ? <Loader2 className="w-8 h-8 animate-spin text-[#00d4ff]" /> : <Plus className="w-8 h-8" />}
                                                <span className="text-[9px] font-bold uppercase tracking-widest mt-2">{isUploading ? 'Subiendo...' : 'Subir Más Fotos'}</span>
                                                <span className="text-[8px] text-gray-500 font-medium mt-1">JPG, PNG o WEBP (máx. 25MB)</span>
                                                <input type="file" multiple accept="image/jpeg,image/png,image/webp,image/jpg" className="hidden" onChange={handleFileUpload} disabled={isUploading} />
                                            </label>
                                            
                                            {formData.gallery.map((item: any, index: number) => (
                                                <div key={item.src} className={`border rounded-[10px] overflow-hidden flex flex-col transition-all ${
                                                    index === 0 
                                                        ? 'border-[#00d4ff] ring-1 ring-[#00d4ff]/40 shadow-lg shadow-[#00d4ff]/5' 
                                                        : 'hover:border-[#00d4ff]/30'
                                                } ${
                                                    isDark ? 'bg-black/20 border-rock' : 'bg-gray-50 border-gray-100 shadow-sm'
                                                }`}>
                                                    {/* Image preview with actions overlay */}
                                                    <div className="h-44 relative group overflow-hidden bg-black/40 flex items-center justify-center border-b border-rock/20">
                                                        <img src={item.src} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" alt="" />
                                                        
                                                        {/* Hover Overlay Actions (Desktop) */}
                                                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                                            {index > 0 && (
                                                                <button 
                                                                    type="button"
                                                                    onClick={() => {
                                                                        const updated = [...formData.gallery];
                                                                        const [moved] = updated.splice(index, 1);
                                                                        updated.unshift(moved);
                                                                        setFormData({ ...formData, gallery: updated });
                                                                    }}
                                                                    className="px-3 py-1.5 bg-white/20 border border-white/30 text-white rounded-[10px] text-[8px] font-bold uppercase hover:bg-[#00d4ff] hover:text-black hover:border-[#00d4ff] transition-all"
                                                                >
                                                                    Hacer Portada
                                                                </button>
                                                            )}
                                                            <button 
                                                                type="button"
                                                                onClick={() => removeImage(item.src)} 
                                                                className="p-3 bg-red-500/80 hover:bg-red-600 rounded-full transition-all text-white hover:scale-110"
                                                                title="Eliminar foto"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>

                                                        {index === 0 && (
                                                            <span className="bg-[#00d4ff] text-black px-2 py-0.5 text-[8px] font-black uppercase rounded-[4px] tracking-wider absolute top-2 left-2 z-10 shadow">
                                                                ⭐ Portada Principal
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* Mobile / Touch-Friendly Quick Action Bar */}
                                                    <div className={`p-2.5 border-b flex items-center justify-between gap-2 ${isDark ? 'border-rock/20 bg-black/30' : 'border-gray-200 bg-gray-100/60'}`}>
                                                        {index === 0 ? (
                                                            <span className="text-[9px] font-black uppercase tracking-wider text-[#00d4ff] flex items-center gap-1">
                                                                <Star className="w-3.5 h-3.5 fill-[#00d4ff]" /> Portada Activa
                                                            </span>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const updated = [...formData.gallery];
                                                                    const [moved] = updated.splice(index, 1);
                                                                    updated.unshift(moved);
                                                                    setFormData({ ...formData, gallery: updated });
                                                                }}
                                                                className="px-2.5 py-1 bg-[#00d4ff]/10 hover:bg-[#00d4ff] text-[#00d4ff] hover:text-black border border-[#00d4ff]/30 hover:border-[#00d4ff] rounded-[6px] text-[8px] font-black uppercase tracking-wider transition-all flex items-center gap-1"
                                                            >
                                                                <Star className="w-3 h-3" /> Hacer Portada
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={() => removeImage(item.src)}
                                                            className="px-2.5 py-1 bg-red-500/10 hover:bg-red-500 text-red-400 hover:text-white border border-red-500/30 hover:border-red-500 rounded-[6px] text-[8px] font-black uppercase tracking-wider transition-all flex items-center gap-1"
                                                        >
                                                            <Trash2 className="w-3 h-3" /> Eliminar
                                                        </button>
                                                    </div>

                                                    {/* Form Fields below Image */}
                                                    <div className="p-4 space-y-3 flex-1 flex flex-col justify-between">
                                                        {/* Object Position / Alignment Selection */}
                                                        <div>
                                                            <label className="text-[8px] font-bold uppercase tracking-widest text-gray-500 mb-1 block">Alineación / Enfoque</label>
                                                            <select
                                                                className={`w-full bg-transparent border-b text-[10px] py-1.5 outline-none focus:border-[#00d4ff] transition-colors ${
                                                                    isDark ? 'border-gray-800 text-white' : 'border-gray-300 text-gray-900'
                                                                }`}
                                                                value={item.position || 'center'}
                                                                onChange={e => {
                                                                    const updated = [...formData.gallery];
                                                                    updated[index] = { ...updated[index], position: e.target.value };
                                                                    setFormData({ ...formData, gallery: updated });
                                                                }}
                                                            >
                                                                <option value="center">Centro (Default)</option>
                                                                <option value="top">Alinear Arriba (Cumbres/Montañas)</option>
                                                                <option value="bottom">Alinear Abajo (Valles/Suelos)</option>
                                                            </select>
                                                        </div>

                                                        {/* Alt Texts */}
                                                        <div className="space-y-1.5 pt-2 border-t border-rock/10">
                                                            <label className="text-[8px] font-bold uppercase tracking-widest text-gray-500 block">Texto alternativo (Alt)</label>
                                                            <input
                                                                type="text"
                                                                placeholder="Alt Español (Ej: El imponente Cerro...)"
                                                                className={`w-full bg-transparent border-b text-[9px] py-1 outline-none focus:border-[#00d4ff] transition-colors ${
                                                                    isDark ? 'border-gray-800 text-white placeholder-gray-700' : 'border-gray-200 text-gray-900 placeholder-gray-400'
                                                                }`}
                                                                value={item.alt_es || ''}
                                                                onChange={e => {
                                                                    const updated = [...formData.gallery];
                                                                    updated[index] = { ...updated[index], alt_es: e.target.value };
                                                                    setFormData({ ...formData, gallery: updated });
                                                                }}
                                                            />
                                                            <input
                                                                type="text"
                                                                placeholder="Alt Inglés (Ej: The imposing Cerro...)"
                                                                className={`w-full bg-transparent border-b text-[9px] py-1 outline-none focus:border-[#00d4ff] transition-colors ${
                                                                    isDark ? 'border-gray-800 text-white placeholder-gray-700' : 'border-gray-200 text-gray-900 placeholder-gray-400'
                                                                }`}
                                                                value={item.alt_en || ''}
                                                                onChange={e => {
                                                                    const updated = [...formData.gallery];
                                                                    updated[index] = { ...updated[index], alt_en: e.target.value };
                                                                    setFormData({ ...formData, gallery: updated });
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeSection === 'pricing' && (
                                <div className="space-y-6 animate-in fade-in duration-500">
                                    <div>
                                        <h3 className="text-lg font-title uppercase tracking-tight font-bold">Configuración de Tarifas & Upgrades</h3>
                                        <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mt-1">Define el precio del servicio (plano o escalonado) y los adicionales opcionales.</p>
                                    </div>

                                    {/* Selector de Tipo de Precio */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-b border-rock/20 pb-6">
                                        <div>
                                            <label className={labelClass}>Tipo de Tarifa</label>
                                            <div className="flex gap-4">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setFormData(prev => ({ ...prev, price_flat: prev.price_flat || 100 }));
                                                    }}
                                                    className={`flex-1 py-3 border text-[10px] font-bold uppercase tracking-widest rounded-[10px] transition-all ${
                                                        formData.price_flat
                                                            ? 'bg-[#00d4ff] border-[#00d4ff] text-black shadow-lg shadow-[#00d4ff]/20'
                                                            : isDark ? 'border-rock text-gray-400 hover:text-white' : 'border-gray-200 text-gray-500 hover:text-gray-900 shadow-sm'
                                                    }`}
                                                >
                                                    Precio Plano / Fijo
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setFormData(prev => ({ ...prev, price_flat: null }));
                                                    }}
                                                    className={`flex-1 py-3 border text-[10px] font-bold uppercase tracking-widest rounded-[10px] transition-all ${
                                                        formData.price_flat === null
                                                            ? 'bg-[#00d4ff] border-[#00d4ff] text-black shadow-lg shadow-[#00d4ff]/20'
                                                            : isDark ? 'border-rock text-gray-400 hover:text-white' : 'border-gray-200 text-gray-500 hover:text-gray-900 shadow-sm'
                                                    }`}
                                                >
                                                    Precios por Tramo (PAX)
                                                </button>
                                            </div>
                                        </div>

                                        {formData.price_flat !== null && (
                                            <div>
                                                <label className={labelClass}>Valor Fijo por Persona (USD)</label>
                                                <input
                                                    type="number"
                                                    className={inputClass}
                                                    value={formData.price_flat || ''}
                                                    onChange={e => setFormData({ ...formData, price_flat: parseFloat(e.target.value) || 0 })}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Precios escalonados (solo si price_flat es nulo) */}
                                    {formData.price_flat === null && (
                                        <div className="space-y-6">
                                            {formData.variants && formData.variants.length > 0 && (
                                                <div className="flex flex-col gap-2">
                                                    <label className={labelClass}>Selecciona la variante o tarifa a configurar:</label>
                                                    <div className="flex flex-wrap gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => setSelectedPricingVariantId('general')}
                                                            className={`px-4 py-2 border text-[10px] font-bold uppercase tracking-widest transition-all rounded-[10px] ${
                                                                selectedPricingVariantId === 'general'
                                                                ? 'bg-[#00d4ff] border-[#00d4ff] text-black shadow-lg shadow-[#00d4ff]/20'
                                                                : isDark 
                                                                    ? 'border-rock text-gray-400 hover:text-white hover:bg-white/5' 
                                                                    : 'border-gray-200 text-gray-500 hover:bg-[#00d4ff]/10 hover:text-gray-900 shadow-sm'
                                                            }`}
                                                        >
                                                            Tarifa General
                                                        </button>
                                                        {formData.variants.map((v: any) => (
                                                            <button
                                                                key={v.id}
                                                                type="button"
                                                                onClick={() => setSelectedPricingVariantId(v.id)}
                                                                className={`px-4 py-2 border text-[10px] font-bold uppercase tracking-widest transition-all rounded-[10px] ${
                                                                    selectedPricingVariantId === v.id
                                                                    ? 'bg-[#00d4ff] border-[#00d4ff] text-black shadow-lg shadow-[#00d4ff]/20'
                                                                    : isDark 
                                                                        ? 'border-rock text-gray-400 hover:text-white hover:bg-white/5' 
                                                                        : 'border-gray-200 text-gray-500 hover:bg-[#00d4ff]/10 hover:text-gray-900 shadow-sm'
                                                                }`}
                                                            >
                                                                {v.name_es}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {selectedPricingVariantId === 'general' && formData.variants && formData.variants.length > 0 && (
                                                <div className={`p-4 border text-[10px] font-bold uppercase tracking-widest leading-relaxed rounded-[10px] ${
                                                    isDark ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' : 'bg-amber-50 border-amber-200 text-amber-800'
                                                }`}>
                                                    Nota: Esta expedición cuenta con variantes específicas. La tarifa general se utilizará únicamente si no se selecciona ninguna variante.
                                                </div>
                                            )}

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {activePricingTiers.map((tier: any, i: number) => (
                                                    <div key={i} className={`p-6 border rounded-[10px] flex justify-between items-center transition-all hover:border-[#00d4ff]/50 ${
                                                        isDark 
                                                        ? 'bg-black/30 border-rock text-white' 
                                                        : 'bg-gray-50 border-gray-200 text-gray-900 shadow-sm'
                                                    }`}>
                                                        <div className="flex items-center gap-3">
                                                            <input 
                                                                type="number" 
                                                                className={`w-16 bg-transparent border-b font-title text-center focus:border-[#00d4ff] outline-none transition-colors ${
                                                                    isDark ? 'border-gray-700 text-white' : 'border-gray-300 text-gray-900'
                                                                }`}
                                                                value={tier.min_people} 
                                                                min="1"
                                                                onChange={e => {
                                                                    const newTiers = [...activePricingTiers];
                                                                    newTiers[i] = { ...newTiers[i], min_people: parseInt(e.target.value) || 1 };
                                                                    updateActivePricingTiers(newTiers);
                                                                }}
                                                            />
                                                            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Pasajeros o más</span>
                                                        </div>
                                                        
                                                        <div className="flex items-center gap-4">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-[10px] text-[#00d4ff] font-black">USD</span>
                                                                <input 
                                                                    type="number" 
                                                                    className={`w-24 bg-transparent border-b font-title text-right focus:border-[#00d4ff] outline-none transition-colors ${
                                                                        isDark ? 'border-gray-700 text-white' : 'border-gray-300 text-gray-900'
                                                                    }`}
                                                                    value={tier.price_per_person} 
                                                                    min="0"
                                                                    onChange={e => {
                                                                        const newTiers = [...activePricingTiers];
                                                                        newTiers[i] = { ...newTiers[i], price_per_person: parseInt(e.target.value) || 0 };
                                                                        updateActivePricingTiers(newTiers);
                                                                    }}
                                                                />
                                                            </div>
                                                            <button 
                                                                type="button"
                                                                onClick={() => {
                                                                    updateActivePricingTiers(activePricingTiers.filter((_: any, idx: number) => idx !== i));
                                                                }} 
                                                                className="p-2 text-gray-500 hover:text-red-500 transition-colors"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}

                                                <button 
                                                    type="button"
                                                    onClick={() => updateActivePricingTiers([...activePricingTiers, { min_people: 1, price_per_person: 0 }].sort((a,b)=>a.min_people - b.min_people))} 
                                                    className={`col-span-1 md:col-span-2 text-[10px] font-black uppercase tracking-widest text-center py-6 border border-dashed rounded-[10px] transition-all duration-200 ${
                                                        isDark 
                                                        ? 'text-[#00d4ff] border-rock hover:border-[#00d4ff] hover:bg-[#00d4ff]/5' 
                                                        : 'text-cyan-600 border-gray-300 hover:border-cyan-500 hover:bg-cyan-50/50 shadow-sm'
                                                    }`}
                                                >
                                                    + Agregar Nivel de Precio (PAX)
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Sección de Upgrades / Adicionales */}
                                    <div className="border-t border-rock/20 pt-6 space-y-4">
                                        <div>
                                            <h4 className="text-sm font-title uppercase tracking-widest text-[#00d4ff]">Servicios Adicionales (Upgrades)</h4>
                                            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mt-1">
                                                Crea opciones extras que el cliente puede marcar para sumar al total de la reserva (ej: transportes opcionales, comidas extras).
                                            </p>
                                        </div>

                                        <div className="space-y-3">
                                            {(formData.price_upgrades || []).map((upg: any, idx: number) => (
                                                <div key={idx} className={`p-4 border rounded-[10px] grid grid-cols-1 md:grid-cols-4 gap-4 items-center ${
                                                    isDark ? 'bg-black/20 border-rock' : 'bg-gray-50 border-gray-200 shadow-sm'
                                                }`}>
                                                    <div className="md:col-span-2">
                                                        <label className="text-[8px] font-bold uppercase tracking-widest text-gray-500 mb-1 block">Nombre del adicional</label>
                                                        <input
                                                            type="text"
                                                            className={inputClass}
                                                            value={upg.name_es}
                                                            onChange={e => {
                                                                const updated = [...formData.price_upgrades];
                                                                updated[idx] = { ...updated[idx], name_es: e.target.value };
                                                                setFormData({ ...formData, price_upgrades: updated });
                                                            }}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-[8px] font-bold uppercase tracking-widest text-gray-500 mb-1 block">Precio por Persona (USD)</label>
                                                        <input
                                                            type="number"
                                                            className={inputClass}
                                                            value={upg.price}
                                                            onChange={e => {
                                                                const updated = [...formData.price_upgrades];
                                                                updated[idx] = { ...updated[idx], price: parseFloat(e.target.value) || 0 };
                                                                setFormData({ ...formData, price_upgrades: updated });
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="flex justify-end pt-5 md:pt-0">
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setFormData({
                                                                    ...formData,
                                                                    price_upgrades: formData.price_upgrades.filter((_: any, i: number) => i !== idx)
                                                                });
                                                            }}
                                                            className="p-2 text-gray-500 hover:text-red-500 transition-colors"
                                                        >
                                                            <Trash2 className="w-5 h-5" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}

                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setFormData({
                                                        ...formData,
                                                        price_upgrades: [...(formData.price_upgrades || []), { name_es: '', name_en: '', price: 0 }]
                                                    });
                                                }}
                                                className={`w-full py-4 text-[10px] font-black uppercase tracking-widest text-center border border-dashed rounded-[10px] transition-all duration-200 bg-black/5 ${
                                                    isDark 
                                                    ? 'text-[#00d4ff] border-rock hover:border-[#00d4ff] hover:bg-[#00d4ff]/5' 
                                                    : 'text-cyan-600 border-gray-300 hover:border-cyan-500 hover:bg-cyan-50/50'
                                                }`}
                                            >
                                                + Agregar Adicional (Upgrade)
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeSection === 'policies' && (
                                <div className="space-y-12 animate-in fade-in duration-500">
                                    <ListEditor 
                                        title="Recomendaciones y Equipamiento" 
                                        list={formData.recommendations_es || []} 
                                        onChange={list => setFormData({...formData, recommendations_es: list})} 
                                        placeholderSuggestions={['Zapatos de trekking (caña alta e impermeables).', 'Ropa técnica por capas.', 'Mochila de expedición (30L mínimo).', 'Protector solar y lentes con filtro UV.', 'Botella de agua reutilizable (1.5L mínimo).']}
                                        isDark={isDark}
                                    />
                                    <div className="border-t border-rock/30 my-8"></div>
                                    <ListEditor 
                                        title="Políticas Específicas" 
                                        list={formData.policies_es || []} 
                                        onChange={list => setFormData({...formData, policies_es: list})} 
                                        placeholderSuggestions={['Actividad sujeta a condiciones climáticas.', 'Política de Cancelación: Reembolso 100% hasta 48 horas antes.', 'Normativa Sin Rastro: Retorno obligatorio de toda la basura.', 'El guía tiene la autoridad para modificar el itinerario por seguridad.']}
                                        isDark={isDark}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="p-4 border-t border-rock flex justify-center bg-black/40 shrink-0">
                        <p className="text-[9px] text-gray-400 uppercase tracking-[0.3em] font-black">Fisura Expeditions &copy; 2026</p>
                    </div>
                </div>

                {/* Notifications overlay inside Portal */}
                {notification && (
                    <div className={`fixed top-10 left-1/2 -translate-x-1/2 z-[200000] px-8 py-5 rounded-[10px] shadow-2xl border-2 backdrop-blur-xl flex items-center gap-4 animate-in slide-in-from-top duration-500 text-white ${
                        notification.type === 'success' ? 'bg-emerald-600 border-emerald-400' : 'bg-red-600 border-red-400'
                    }`}>
                        {notification.type === 'success' ? <CheckCircle2 className="w-6 h-6" /> : <AlertCircle className="w-6 h-6" />}
                        <p className="text-[12px] font-black uppercase tracking-widest">{notification.message}</p>
                    </div>
                )}

                {/* Confirmation overlay inside Portal */}
                {customConfirm && createPortal(
                    <div className="fixed inset-0 z-[2000000] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-300">
                        <div className="w-full max-w-sm p-8 rounded-[10px] border border-rock bg-[#1a1512] text-white flex flex-col items-center text-center gap-6 shadow-[0_40px_100px_rgba(0,0,0,0.7)]">
                            <AlertCircle className="w-12 h-12 text-red-500" />
                            <h3 className="text-xl font-title uppercase tracking-tight">¿Confirmar Acción?</h3>
                            <p className="text-xs text-gray-400 font-bold uppercase tracking-widest leading-relaxed">{customConfirm.message}</p>
                            <div className="flex flex-col w-full gap-3 pt-4">
                                <button onClick={() => { customConfirm.onConfirm(); setCustomConfirm(null); }} className="bg-red-500 text-white py-4 rounded-[10px] font-title text-sm uppercase tracking-widest hover:bg-red-600 transition-all shadow-lg shadow-red-500/20">Confirmar</button>
                                <button onClick={() => setCustomConfirm(null)} className="py-2 text-[10px] font-bold uppercase tracking-widest opacity-50 hover:opacity-100 transition-all">Cancelar</button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
            </div>
        </>
    , document.body);
};

export default ExpeditionEditor;
